import { Link } from 'react-router-dom';
import { ArrowRight, Compass, Wrench } from 'lucide-react';
import {
  PageShell,
  Section,
  SectionLabel,
  Heading,
  Card,
  CardHeader,
  RowList,
  Row,
  Badge,
  Divider,
} from '../../components/elevation';
import { useMethodIntroEnabled } from '../../features/method/hooks/useMethodIntroEnabled';
import { METHOD_PHASES } from '../../lib/method/content';

export default function MethodHome() {
  const introEnabled = useMethodIntroEnabled();

  return (
    <PageShell>
      <Section>
        <SectionLabel>The Method</SectionLabel>
        <Heading as="h1" size="xl" className="mb-4">
          Priorities.ai is built on Managing Priorities
        </Heading>
        <p className="text-charcoal-700 dark:text-charcoal-300 max-w-3xl leading-relaxed">
          Priorities.ai is guided by the DEGAP methodology from <em>Managing Priorities</em>, by
          Harry Max: Decide, Engage, Gather, Arrange, Prioritize. The app uses DEGAP to help teams
          clarify intent, involve the right people, gather the right inputs, arrange meaningful
          comparisons, and form priorities that can hold under execution pressure.
        </p>
        {introEnabled && (
          <div className="flex items-center gap-3 mt-6">
            <Link
              to="/elevation/method/tour"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white transition-colors"
            >
              <Compass className="h-4 w-4" />
              Take the tour
            </Link>
            <span className="text-sm text-charcoal-500 dark:text-charcoal-400">
              A 5-minute walk through each phase, with links to where it happens in the app.
            </span>
          </div>
        )}
      </Section>

      <Section>
        <SectionLabel>The five phases</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {METHOD_PHASES.map((p) => (
            <Link key={p.phase} to={`/elevation/method/${p.phase}`} className="group block h-full">
              <div
                className="h-full p-5 border border-charcoal-200 dark:border-charcoal-700 transition-colors flex flex-col gap-3"
                style={{ backgroundColor: p.tint }}
              >
                <span
                  className="font-mono text-[10px] uppercase tracking-wider"
                  style={{ color: p.color }}
                >
                  Phase {p.order} of 5
                </span>
                <h3 className="font-heading font-semibold text-base" style={{ color: p.color }}>
                  {p.title}
                </h3>
                <span className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-charcoal-600 group-hover:gap-2 transition-all">
                  Learn this phase <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <Section>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card flush>
            <CardHeader label="REFERENCE" title="Six kinds of things you can prioritize" />
            <RowList>
              <Row>
                <span className="flex-1">Activities, People, Places, Things, Information, Time</span>
                <Link
                  to="/elevation/method/object-types"
                  className="text-primary-600 hover:text-primary-700 text-xs font-medium inline-flex items-center gap-1"
                >
                  View <ArrowRight className="h-3 w-3" />
                </Link>
              </Row>
            </RowList>
          </Card>
          <Card flush>
            <CardHeader label="REFERENCE" title="The technique toolbox" count={16} />
            <RowList>
              <Row>
                <Wrench className="h-4 w-4 text-charcoal-400" />
                <span className="flex-1">
                  RICE, WSJF, Eisenhower, and more — each with a working tool
                </span>
                <Link
                  to="/elevation/method/toolbox"
                  className="text-primary-600 hover:text-primary-700 text-xs font-medium inline-flex items-center gap-1"
                >
                  Browse <ArrowRight className="h-3 w-3" />
                </Link>
              </Row>
            </RowList>
          </Card>
        </div>
      </Section>

      <Divider />

      <Section className="mb-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-charcoal-500 dark:text-charcoal-400">
            Adapted from <em>Managing Priorities: How to Create Better Plans and Make Smarter
            Decisions</em> by Harry Max.
          </p>
          <Badge variant="outline">
            <Link to="/elevation/glossary" className="hover:text-primary-600">
              Full glossary →
            </Link>
          </Badge>
        </div>
      </Section>
    </PageShell>
  );
}
